import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { assetUrl } from "@/lib/api";
import { useBranch } from "@/contexts/BranchContext";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
    Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Truck, Download, Eye, Calendar, CheckCircle2, XCircle, FileText, Loader2 } from "lucide-react";

const VEHICLE_TYPE_LABELS = {
    camion: "Camión", panel: "Panel", pickup: "Pickup",
    furgon: "Furgón", cisterna: "Cisterna", otro: "Otro",
};

export default function PortalTransportes() {
    const { branchParam } = useBranch();
    const [searchParams, setSearchParams] = useSearchParams();
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedService, setSelectedService] = useState(null);
    const [certs, setCerts] = useState({});
    const [certLoading, setCertLoading] = useState(null);

    useEffect(() => {
        if (!selectedService) { setCerts({}); return; }
        api.get(`/transport/services/${selectedService.id}/certificates`)
            .then((r) => setCerts(r.data || {}))
            .catch(() => setCerts({}));
    }, [selectedService]);

    const generateCert = async (vehicleId) => {
        if (!selectedService) return;
        setCertLoading(vehicleId);
        try {
            const r = await api.post(`/transport/services/${selectedService.id}/certificate`, { vehicle_id: vehicleId });
            setCerts((c) => ({ ...c, [vehicleId]: r.data.pdf_url }));
            window.open(assetUrl(r.data.pdf_url), "_blank");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error al generar certificado");
        } finally {
            setCertLoading(null);
        }
    };


    useEffect(() => {
        const load = async () => {
            try {
                const r = await api.get("/transport/services", { params: branchParam });
                const data = r.data;
                setServices(data);

                // Auto-open dialog from ?open=<service_id> (Silvia deep-link)
                const openId = searchParams.get("open");
                if (openId && data.length > 0) {
                    const match = data.find(s => s.id === openId);
                    if (match) setSelectedService(match);
                    setSearchParams({}, { replace: true });
                }
            } catch (e) {
                toast.error("Error al cargar servicios de transporte");
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [searchParams, setSearchParams, branchParam]);

    if (loading) return <div className="py-10 text-center text-muted-foreground">Cargando...</div>;

    return (
        <div data-testid="portal-transportes">
            <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-[#CC0000]/10 flex items-center justify-center">
                    <Truck className="w-5 h-5 text-[#CC0000]" />
                </div>
                <div>
                    <h1 className="text-2xl font-semibold" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>
                        Control de Transportes
                    </h1>
                    <p className="text-sm text-muted-foreground">
                        Historial de tratamientos de control de rastreros en vehículos
                    </p>
                </div>
            </div>

            {services.length === 0 ? (
                <Card className="p-10 text-center">
                    <Truck className="w-12 h-12 mx-auto text-muted-foreground mb-3 opacity-50" />
                    <p className="text-muted-foreground">No hay servicios de transporte registrados</p>
                </Card>
            ) : (
                <div className="space-y-3">
                    {services.map((s) => {
                        const total = s.treatments?.length || 0;
                        const treated = s.treatments?.filter((t) => t.service_done).length || 0;
                        const pct = total > 0 ? Math.round((treated / total) * 100) : 0;

                        return (
                            <Card
                                key={s.id}
                                className="p-4 hover:border-[#CC0000]/30 transition-colors cursor-pointer"
                                onClick={() => setSelectedService(s)}
                                data-testid={`transport-card-${s.id}`}
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                                            <Calendar className="w-5 h-5 text-slate-600" />
                                        </div>
                                        <div>
                                            <div className="font-mono font-semibold">{s.service_date}</div>
                                            <div className="text-xs text-muted-foreground">
                                                Realizado por: {s.performed_by || "—"}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <div className="text-right">
                                            <div className="flex items-center gap-1.5">
                                                <span className="text-lg font-bold text-green-700">{treated}</span>
                                                <span className="text-muted-foreground">/</span>
                                                <span className="text-lg font-bold">{total}</span>
                                            </div>
                                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                                                {pct}% tratados
                                            </div>
                                        </div>
                                        {s.pdf_url && (
                                            <a
                                                href={assetUrl(s.pdf_url)}
                                                target="_blank"
                                                rel="noreferrer"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <Button variant="outline" size="icon" data-testid={`transport-download-${s.id}`}>
                                                    <Download className="w-4 h-4" />
                                                </Button>
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </Card>
                        );
                    })}
                </div>
            )}

            {/* Detail Dialog */}
            <Dialog open={!!selectedService} onOpenChange={() => setSelectedService(null)}>
                <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                    {selectedService && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="flex items-center gap-2">
                                    <Truck className="w-5 h-5 text-[#CC0000]" />
                                    Detalle del Servicio — {selectedService.service_date}
                                </DialogTitle>
                            </DialogHeader>

                            <div className="grid grid-cols-3 gap-3 mb-4">
                                <div className="rounded-lg bg-slate-50 p-3 text-center">
                                    <div className="text-2xl font-bold">{selectedService.treatments?.length || 0}</div>
                                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total</div>
                                </div>
                                <div className="rounded-lg bg-green-50 p-3 text-center">
                                    <div className="text-2xl font-bold text-green-700">
                                        {selectedService.treatments?.filter((t) => t.service_done).length || 0}
                                    </div>
                                    <div className="text-[10px] uppercase tracking-wider text-green-600">Tratados</div>
                                </div>
                                <div className="rounded-lg bg-orange-50 p-3 text-center">
                                    <div className="text-2xl font-bold text-orange-600">
                                        {(selectedService.treatments?.length || 0) - (selectedService.treatments?.filter((t) => t.service_done).length || 0)}
                                    </div>
                                    <div className="text-[10px] uppercase tracking-wider text-orange-500">Sin tratar</div>
                                </div>
                            </div>

                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Placa</TableHead>
                                        <TableHead>Tipo</TableHead>
                                        <TableHead>Marca</TableHead>
                                        <TableHead>Servicio</TableHead>
                                        <TableHead>Productos</TableHead>
                                        <TableHead>Obs.</TableHead>
                                        <TableHead className="text-center">Certificado</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {(selectedService.treatments || []).map((t, i) => (
                                        <TableRow key={i}>
                                            <TableCell className="font-mono font-semibold">{t.plate_number}</TableCell>
                                            <TableCell className="text-sm">{VEHICLE_TYPE_LABELS[t.vehicle_type] || t.vehicle_type}</TableCell>
                                            <TableCell className="text-sm">{t.brand || "—"}</TableCell>
                                            <TableCell>
                                                {t.service_done ? (
                                                    <div className="flex items-center gap-1 text-green-700">
                                                        <CheckCircle2 className="w-4 h-4" />
                                                        <span className="text-xs font-semibold">SI</span>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center gap-1 text-orange-600">
                                                        <XCircle className="w-4 h-4" />
                                                        <span className="text-xs font-semibold">NO</span>
                                                    </div>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-xs">
                                                {t.products_applied?.length > 0
                                                    ? t.products_applied.map((p) => p.product_name).join(", ")
                                                    : "—"}
                                            </TableCell>
                                            <TableCell className="text-xs text-muted-foreground max-w-[120px] truncate">
                                                {t.observations || "—"}
                                            </TableCell>
                                            <TableCell className="text-center">
                                                {!t.service_done ? (
                                                    <span className="text-xs text-muted-foreground">—</span>
                                                ) : certs[t.vehicle_id] ? (
                                                    <a href={assetUrl(certs[t.vehicle_id])} target="_blank" rel="noreferrer">
                                                        <Button variant="outline" size="sm" className="h-7 text-[11px] gap-1" data-testid={`cert-download-${t.vehicle_id}`}>
                                                            <Download className="w-3 h-3" /> Ver
                                                        </Button>
                                                    </a>
                                                ) : (
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        className="h-7 text-[11px] gap-1 border-[#CC0000]/40 text-[#CC0000] hover:bg-[#CC0000]/5"
                                                        disabled={certLoading === t.vehicle_id}
                                                        onClick={() => generateCert(t.vehicle_id)}
                                                        data-testid={`cert-generate-${t.vehicle_id}`}
                                                    >
                                                        {certLoading === t.vehicle_id
                                                            ? <Loader2 className="w-3 h-3 animate-spin" />
                                                            : <FileText className="w-3 h-3" />}
                                                        Generar
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>

                            {selectedService.general_observations && (
                                <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                                    <p className="text-xs uppercase tracking-wider text-amber-700 font-semibold mb-1">Observaciones generales</p>
                                    <p className="text-sm">{selectedService.general_observations}</p>
                                </div>
                            )}

                            <div className="grid grid-cols-2 gap-4 mt-3 text-sm">
                                <div>
                                    <span className="text-xs text-muted-foreground">Realizado por:</span>
                                    <p className="font-medium">{selectedService.performed_by || "—"}</p>
                                </div>
                                <div>
                                    <span className="text-xs text-muted-foreground">Aceptado por:</span>
                                    <p className="font-medium">{selectedService.accepted_by || "—"}</p>
                                </div>
                            </div>

                            {selectedService.pdf_url && (
                                <div className="mt-4 flex justify-center">
                                    <a href={assetUrl(selectedService.pdf_url)} target="_blank" rel="noreferrer">
                                        <Button className="bg-[#CC0000] hover:bg-[#CC0000]/90 text-white" data-testid="transport-detail-download">
                                            <Download className="w-4 h-4 mr-2" /> Descargar PDF
                                        </Button>
                                    </a>
                                </div>
                            )}
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
